import { Injectable } from '@angular/core';
import { Offer } from '../model/offer';
import { BsModalService } from 'ngx-bootstrap/modal';
import { UpgradeAvailabilityDTO, countTrue } from '../model/upgradeAvailabilityDTO';
import { Application } from 'express';
import { ApplicationConfig } from '@app/model/guestDTO';
import { OfferCategory } from '../model/offer-category.model';
import { OfferCategoryService } from './offer-category.service';

export type UiOfferTab = OfferCategory;

@Injectable({
  providedIn: 'root'
})
export class SharedService {
  constructor(private offerCategoryService: OfferCategoryService) {}


  // public offerAliasId: string = ''; // set by home component, This is the selected user offer alias id
  // public propId: string = ''; // set by home component, This is the selected user property id
  public propertyName: string = ''; // set home component, This is the selected user property name
  public guestFirstName: string = ''; // set by home component, This is the selected user guest first name
  public guestLastName: string = ''; // set by home component, This is the selected user guest first name
  public baseRoomCategoryName: string = ''; // set by home component, This is the selected user base room category name
  public arrivalDate: Date = new Date(); // set by home component, This is the selected user arrival date
  // public departureDate: Date = new Date(); // set by home component, This is the selected user arrival date
  public lengthOfStay: number = 0; // set by home component, This is the selected user length of stay
  public confirmationNumber: string = ''; // set by home component, This is the selected user departure date
  public hasGuestEmail: boolean = false; // set by home component, This is the selected user has guest email
  public offerSetFilter: OfferCategory = 'ROOM'; // set by offer-nav-bar component, This is the selected user offer set filter: room, eci, lco, addon
  /** Current active offer type/tab selection for the UI (room/eci/lco/addon). */
  public activeOfferType: UiOfferTab = 'ROOM';
  public applicationConfig: ApplicationConfig | null = null; // set by home component, This is the selected user application config from guest info
  public flg_tiles_view_default: boolean = true; // set by home component, This is the selected user default view mode flag
  public flg_light_mode_default: boolean = false; // set by home component, This is the selected user default light mode flag
  public autoSequenceEnabled: boolean = false; // set by theme controls, This is the auto sequence toggle value from theme controls

  public offer!:Offer; // set by home component, This is the selected user offer
  public offers!:Offer[]; // set by home component
  public offerSets: { offerSetId: string; offerSet: Offer[] }[] = [{ offerSetId: '', offerSet: [] }]; // Offer[]

  public offerCount: number = 0;
  public ts_offer_expires: string = '';
  public viewMoreToggle:boolean = false;
  public ts_expires: Date = new Date();
  public numOffersVisible: number = 3; // Number of offers visible in the offer card

  public requestOnlyMode: boolean = false; // If true, only request button is shown, no offer details

  public guestEamil: string = '';
  public currentIndex:number = -1;
  public currentOfferIndex:number = -1;
  public modalService!: BsModalService;

  public config = {
    animated: true,
    keyboard: true,
    backdrop: false,
    ignoreBackdropClick: false,
    class: 'modal-dialog-centered modal-photo-gallery modal-lg'
  };

  /**
   * Session-only favorites store.
   * Keyed by offerAliasId so different guests/sessions don't bleed into each other.
   */
  private favoriteOfferIdsByAlias: Record<string, Set<string>> = {};

  private getFavoriteSetForAlias(offerAliasId: string): Set<string> {
    if (!offerAliasId) offerAliasId = 'default';
    if (!this.favoriteOfferIdsByAlias[offerAliasId]) {
      this.favoriteOfferIdsByAlias[offerAliasId] = new Set<string>();
    }
    return this.favoriteOfferIdsByAlias[offerAliasId];
  }

  /** Apply session favorites to a list of offers (mutates offer.flg_favorite). */
  public applyFavoritesToOffers(offerAliasId: string, offers: Offer[] | null | undefined): void {
    if (!offers || !Array.isArray(offers)) return;
    const favs = this.getFavoriteSetForAlias(offerAliasId);
    for (const o of offers) {
      if (!o?.offer_id) continue;
      o.flg_favorite = favs.has(o.offer_id);
    }
  }

  setSelectedOffer(index: number, offers: Offer[], expandContent: boolean){
    if(!offers) return;

    if(expandContent) { // Prevents auto expand if user selects request without viewing offer details
      this.currentIndex = index;
    }

    this.currentOfferIndex = index;
    this.offer = offers[index];
  }

  setSelectedOfferByOffer(index:number, offer: Offer, expandContent: boolean){
    if(!offer) return;

    if(expandContent) { // Prevents auto expand if user selects request without viewing offer details
      this.currentIndex = index;
    }

    this.currentOfferIndex = index;
    this.offer = offer;
  }

  public offerFilters: Record<OfferCategory, boolean> = {
    ROOM: true,
    ECI: false,
    LCO: false,
    ANCILLARY: false,
  }

  /**
   * Store and broadcast the active offer type.
   * This is intentionally separate from offerFilters so other parts of the app
   * can easily read a single value.
   */
  public setActiveOfferType(tab: UiOfferTab): void {
    this.activeOfferType = tab;

    // Optional persistence: makes the value available across refreshes.
    // Safe to ignore if storage is unavailable.
    try {
      sessionStorage.setItem('activeOfferType', tab);
    } catch {
      // ignore
    }
  }

  /** Tracks whether user has already visited/seen each upgrade tab (used for next-tab navigation). */
  public hasSeenUpgradeOffer: Record<UiOfferTab, boolean> = {
    ROOM: false,
    ECI: false,
    LCO: false,
    ANCILLARY: false,
  };

  /**
   * Auto-sequence toggle from Theme Controls.
   * When OFF, we block all *programmatic* tab navigation.
   * Default is ON when the setting is missing.
   */
  public isAutoSequenceEnabled(): boolean {
    try {
      if (typeof window === 'undefined') return true;
      const value = sessionStorage.getItem('autoSequence');
      if (value === null) return true;
      return value === 'on';
    } catch {
      return true;
    }
  }

  public markUpgradeTabSeen(tab: UiOfferTab): void {
    this.hasSeenUpgradeOffer[tab] = true;
  }

  public resetSeenUpgradeTabs(): void {
    (Object.keys(this.hasSeenUpgradeOffer) as UiOfferTab[]).forEach(k => (this.hasSeenUpgradeOffer[k] = false));
  }

  /**
   * Switches the active tab in shared state and notifies the UI New home component.
   * Uses a CustomEvent for cross-component communication.
   */
  public navigateToOfferTab(tab: UiOfferTab): void {
    // Gate all programmatic navigation when auto-sequence is disabled.
    // Initial tab selection on load does not use this method.
    if (!this.isAutoSequenceEnabled()) {
      return;
    }

    this.setOfferFilter(tab);
    this.markUpgradeTabSeen(tab);

    // Preferred path
    try {
      window.dispatchEvent(new CustomEvent('ui:new:navigateTab', { detail: { tab }, bubbles: true }));
      return;
    } catch {
      // ignore and try fallback
    }

    // Fallback for runtimes where CustomEvent constructor isn't supported
    try {
      const evt = document.createEvent('CustomEvent');
      evt.initCustomEvent('ui:new:navigateTab', true, true, { tab });
      window.dispatchEvent(evt);
    } catch {
      // last resort: shared state already updated
    }
  }

  /**
   * Determines what the next UI tab action should be.
   * The caller is responsible for performing checkout when action is 'checkout'.
   */
  public getNextUpgradeAction(offersInCart: number): 
  { action: 'checkout' } | { action: 'navigate'; tab: UiOfferTab } | null {
  
    if (!this.isAutoSequenceEnabled()) {
      return null;
    }

    const upgradeAvailability = this.getUpgradeAvailability();
    const availCount = this.countUpgradeTypesAvailable(upgradeAvailability!);
    const addon_available: boolean = upgradeAvailability?.addon_available ?? false;
    
    if (addon_available == false && offersInCart >= availCount) {
      return { action: 'checkout' };
    }    

    const activeTab = this.getOfferCurrentActiveFilter();

    if (!upgradeAvailability) return null;

    const next = this.offerCategoryService.getNextCategoryInSequence(
      activeTab,
      this.hasSeenUpgradeOffer,
      upgradeAvailability,
    );

    if (next) {
      return { action: 'navigate', tab: next };
    }

    // Nothing left to show; let caller decide what to do.
    return null;
  }

  public setOfferFilter(filter: string) {
    const normalized = this.offerCategoryService.normalizeCategory(filter, 'ROOM');

    // Keep the single-value active offer type in sync.
    this.setActiveOfferType(normalized);
    this.offerSetFilter = normalized;

    (Object.keys(this.offerFilters) as OfferCategory[]).forEach(key => {
      this.offerFilters[key] = key === normalized;
    });
  }

  public getOfferFilterActive(filter: string){
    const key = this.offerCategoryService.normalizeCategory(filter, 'ROOM');
    return this.offerFilters[key];
  }

  public getOfferCurrentActiveFilter(): UiOfferTab {
    for (const key of Object.keys(this.offerFilters) as OfferCategory[]) {
      if (this.offerFilters[key]) {
        return key;
      }
    }
    return 'ROOM'; // default
  }

  upgradeAvailability: UpgradeAvailabilityDTO | null = null;
  public setUpgradeAvailability(upgradeAvailabilityDTO: UpgradeAvailabilityDTO) {
    // localStorage.setItem('upgradeAvailability', JSON.stringify(upgradeAvailabilityDTO));
    this.upgradeAvailability = upgradeAvailabilityDTO;
  }

  public getUpgradeAvailability(): UpgradeAvailabilityDTO | null {
    return this.upgradeAvailability;
  }

  // Check the number of upgrade types available ie. rooms, eci, lco, addon
  public countUpgradeTypesAvailable(upgradeAvailability: UpgradeAvailabilityDTO): number {
    if (!upgradeAvailability) return 0;
    return countTrue(upgradeAvailability);
  }


  public get(filter: string) {
    this.setOfferFilter(filter);
  }


  public setOfferTsExpires(ts_expires: string){
    this.ts_offer_expires = ts_expires;
    this.ts_expires = new Date(ts_expires);
  }

  public setSelectedOfferSet(offer_set: Offer[]){
    this.offers = offer_set;
    this.offerCount = offer_set.length;
  }

  public addOfferSet(offerSetId: string, offerSet: Offer[]) {
    if (!offerSet || offerSet.length === 0) return;

    const exists = this.offerSets.some(set => set.offerSetId === offerSetId);
    if (!exists) {
      const newSet = { offerSetId, offerSet };
      this.offerSets.push(newSet);
    }
  }

  public offerFilterOfferSetIds: Record<OfferCategory, string> = {
    ROOM: '',
    ECI: '',
    LCO: '',
    ANCILLARY: '',
  }

  public setOfferFilterOfferSetId(filter: string, offerSetId: string) {
    const normalized = this.offerCategoryService.normalizeCategory(filter, 'ROOM');
    this.offerFilterOfferSetIds[normalized] = offerSetId;
  }

  filterDefaultAndValidOffers(offers: Offer[]): Offer[]{

    const defaultOffers = offers.filter(ofr => ofr.flg_default_offer === true);
    const validUpsellOffers = offers.filter(ofr => ofr.flg_valid_upsell === true);

    if(offers){
      if(defaultOffers.length < 3 && defaultOffers.length > 0){
        return validUpsellOffers
                     .sort((a, b) => (a.display_order ?? 0) - (b.display_order ?? 0))
                     .slice(0, 3);
      } 
      else if(defaultOffers.length == 0){
        return validUpsellOffers;
      } else if(defaultOffers.length > 0){
        return defaultOffers;
      } else if (validUpsellOffers.length > 0) {
          return validUpsellOffers;
      }
    }
    return offers;
  }

  toggleFavorite(offerId: string): boolean {
    if (!offerId) return false;

    const alias = (this.offer?.offer_alias_id || (this.offers?.[0]?.offer_alias_id) || 'default') as string;
    const favs = this.getFavoriteSetForAlias(alias);
    const isNowFav = favs.has(offerId) ? (favs.delete(offerId), false) : (favs.add(offerId), true);

    // Update current in-memory offers list (if present)
    const found = (this.offers ?? []).find(o => o?.offer_id === offerId);
    if (found) found.flg_favorite = isNowFav;
    if (this.offer?.offer_id === offerId) this.offer.flg_favorite = isNowFav;

    return isNowFav;
  }



}
